import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
// Import Components
import { BusyLoader, Empty } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Constants
import { l } from 'constants/common';
// Import Services
import { Helpers } from 'services';
// Import Enums
import { FormatNumberActionEnum } from 'enums';

const { formatNumber } = Helpers;

interface CountsByStatus {
    labelKey: string;
    value: Nullable<number>;
}

interface CampaignReportBonusCountsByStatusesProps {
    countsByStatuses: CountsByStatus[];
    totalAmountInUserCurrency?: Nullable<number>;
    userCurrencyId?: string;
    isLoading: boolean;
    emptySize?: 'small' | 'big' | 'medium';
}

const CampaignReportBonusCountsByStatuses: FC<CampaignReportBonusCountsByStatusesProps> = (props) => {
    const { countsByStatuses, totalAmountInUserCurrency, userCurrencyId, isLoading, emptySize = 'small' } = props;
    const { t } = useTranslation();

    return (
        <SectionWithTitle
            title={t(l.BonusSummaryByStatus)}
            hasContentPadding={false}
            className="campaign-report-bonus-summary-by-status"
            contentClassName="campaign-report-bonus-summary-by-status-content"
        >
            <div className="campaign-report-bonus-calculation-logic-scrollable-container">
                <div className="bc-grid-layout-container">
                    <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
                        {isEmpty(countsByStatuses) ? (
                            <div className="empty-container">
                                <Empty title={t(l.NoDataToDisplay)} size={emptySize} />
                            </div>
                        ) : (
                            <>
                                {countsByStatuses.map((item, index) => (
                                    <div className="key-value-v bc-grid-layout-item-4" key={index}>
                                        <span className="ellipsis-text kv-label">{t(item.labelKey)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {formatNumber(item.value, {
                                                type: FormatNumberActionEnum.DecimalPrecision,
                                                precision: 0,
                                            })}
                                        </span>
                                    </div>
                                ))}
                                <div className="key-value-v bc-grid-layout-item-4">
                                    <span className="ellipsis-text kv-label">
                                        {t(l.TotalAmountInCurrency, { currencyId: userCurrencyId })}
                                    </span>
                                    <span className="ellipsis-text kv-value kv-value-css">
                                        {isNil(totalAmountInUserCurrency)
                                            ? t(l.NA)
                                            : formatNumber(totalAmountInUserCurrency, {
                                                  type: FormatNumberActionEnum.DecimalPrecision,
                                                  precision: 2,
                                              })}
                                    </span>
                                </div>
                            </>
                        )}
                    </BusyLoader>
                </div>
            </div>
        </SectionWithTitle>
    );
};

export default memo(CampaignReportBonusCountsByStatuses);
